import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { useAppSelector } from 'redux/store-hooks';
import { LearnMoreCreatorState } from 'redux/types/learnMoreCreatorState.types';
import { surveyAdminRequests } from 'redux/requests/survey-admin.requests';
import { createApiThunk } from 'redux/utils/api-thunk';
import {
  DEFAULT_LEARN_MORE_CONTENT,
  LearnMoreContentDto,
} from '@act/shared/data-transfer-objects';
import { SurveyAdmin } from '@act/shared/data-transfer-objects';

// THUNKS/ACTIONS ==============================================================
export const createLearnMoreCreatorRecord = createApiThunk(
  surveyAdminRequests.createLearnMoreCreatorRecord
);
export const deleteLearnMoreCreatorRecord = createApiThunk(
  surveyAdminRequests.deleteLearnMoreCreatorRecord
);
export const updateLearnMoreCreatorRecord = createApiThunk(
  surveyAdminRequests.updateLearnMoreCreatorRecord
);
export const getLearnMoreRecordById = createApiThunk(
  surveyAdminRequests.getLearnMoreRecordById
);
export const getLearnMoreRecords = createApiThunk(
  surveyAdminRequests.getLearnMoreRecords
);

// INITIAL STATE ===============================================================
const learnMoreCreatorRecordAdapter =
  createEntityAdapter<SurveyAdmin.LearnMoreCreatorRecordDto>();

const initialState: LearnMoreCreatorState = {
  status: 'initial',
  currentContent: DEFAULT_LEARN_MORE_CONTENT,
  records: learnMoreCreatorRecordAdapter.getInitialState(),
};

// SLICE =======================================================================
export const learnMoreCreatorSlice = createSlice({
  name: 'learnMoreCreator',
  initialState,
  reducers: {
    setCurrentContent: (
      state,
      action: PayloadAction<LearnMoreContentDto & { id?: number }>
    ) => {
      state.currentContent = action.payload;
    },
    setCurrentContentById: (state, action: PayloadAction<number>) => {
      const record = state.records.entities[action.payload];
      if (record) {
        state.currentContent = {
          ...record.learnMoreContent,
          id: action.payload,
        };
      }
    },
    resetCurrentContent: state => {
      state.currentContent = DEFAULT_LEARN_MORE_CONTENT;
    },
    upsertLearnMoreRecord: (
      state,
      action: PayloadAction<SurveyAdmin.LearnMoreCreatorRecordDto>
    ) => {
      learnMoreCreatorRecordAdapter.upsertOne(state.records, action.payload);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getLearnMoreRecords.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getLearnMoreRecords.fulfilled,
        (
          state,
          action: PayloadAction<SurveyAdmin.LearnMoreCreatorRecordDto[]>
        ) => {
          state.status = 'success';
          learnMoreCreatorRecordAdapter.upsertMany(
            state.records,
            action.payload
          );
        }
      )
      .addCase(getLearnMoreRecords.rejected, state => {
        state.status = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const learnMoreCreatorReducer = learnMoreCreatorSlice.reducer;
export const {
  resetCurrentContent,
  setCurrentContent,
  setCurrentContentById,
  upsertLearnMoreRecord,
} = learnMoreCreatorSlice.actions;

// SELECTORS ===================================================================
export const selectLearnMoreCreatorState = state =>
  (state as { learnMoreCreator: typeof initialState })?.learnMoreCreator;

export const selectLearnMoreCreatorStateStatus = state =>
  (state as { learnMoreCreator: typeof initialState })?.learnMoreCreator
    ?.status;

export const selectLearnMoreCreatorCurrentContent = state =>
  (state as { learnMoreCreator: typeof initialState })?.learnMoreCreator
    ?.currentContent;

export const learnMoreCreatorRecordSelectors =
  learnMoreCreatorRecordAdapter.getSelectors<LearnMoreCreatorState>(
    state => state.records
  );

export const selectAllLearnMoreRecords = createSelector(
  selectLearnMoreCreatorState,
  learnMoreCreatorRecordSelectors.selectAll
);

// EXPORT HELPER ===============================================================
export const learnMoreCreatorSelectors = {
  useSelectLearnMoreCreatorStatus: () =>
    useAppSelector(selectLearnMoreCreatorStateStatus),
  useSelectLearnMoreCurrentContent: () =>
    useAppSelector(selectLearnMoreCreatorCurrentContent),
  useSelectLearnMoreRecords: () => useAppSelector(selectAllLearnMoreRecords),
};

// EFFECTS =====================================================================

// WEBSOCKET ACTIONS ===========================================================
